import React from 'react';
import { LiveSignalStats } from '../../../hooks/v2/useLessonControlData';

type BucketTone = 'slate' | 'sky' | 'amber' | 'rose' | 'emerald';

interface BucketChipDefinition {
  key: string;
  label: string;
  count: number;
  tone: BucketTone;
}

const chipToneClasses: Record<BucketTone, { active: string; idle: string }> = {
  slate: {
    active: 'border-slate-400 bg-slate-100 text-slate-900',
    idle: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
  },
  sky: {
    active: 'border-sky-300 bg-sky-100 text-sky-900',
    idle: 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100',
  },
  amber: {
    active: 'border-amber-300 bg-amber-100 text-amber-900',
    idle: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
  },
  rose: {
    active: 'border-rose-300 bg-rose-100 text-rose-900',
    idle: 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100',
  },
  emerald: {
    active: 'border-emerald-300 bg-emerald-100 text-emerald-900',
    idle: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
  },
};

interface TeacherSignalsPanelProps {
  currentStepTitle: string;
  liveSignalStats: LiveSignalStats | null;
  activeBucket: string | null;
  bucketNames: string[];
  hasAnswerKey: boolean;
  blanksTotal: number;
  blanksKeyed: number;
  currentStepHasSignal: boolean;
  currentStepRevealMode: string;
  revealSubmitting: boolean;
  revealSuccess: boolean;
  revealError: string | null;
  onSelectBucket: (bucketKey: string | null) => void;
  onRevealAnswers: () => void;
}

const renderBucketChip = (
  definition: BucketChipDefinition,
  activeBucket: string | null,
  onSelectBucket: (bucketKey: string | null) => void,
) => {
  const tone = chipToneClasses[definition.tone];
  const selected = activeBucket === definition.key;
  return (
    <button
      key={`signal-bucket-${definition.key}`}
      type="button"
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${
        selected ? tone.active : tone.idle
      }`}
      onClick={() => onSelectBucket(selected ? null : definition.key)}
    >
      <span>{definition.label}</span>
      <span className="font-semibold">{definition.count}</span>
    </button>
  );
};

export const TeacherSignalsPanel: React.FC<TeacherSignalsPanelProps> = ({
  currentStepTitle,
  liveSignalStats,
  activeBucket,
  bucketNames,
  hasAnswerKey,
  blanksTotal,
  blanksKeyed,
  currentStepHasSignal,
  currentStepRevealMode,
  revealSubmitting,
  revealSuccess,
  revealError,
  onSelectBucket,
  onRevealAnswers,
}) => {
  if (!liveSignalStats) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-500 shadow-sm">
        Loading live signals...
      </div>
    );
  }

  const primaryBuckets: BucketChipDefinition[] = [
    {
      key: 'submitted',
      label: 'Submitted',
      count: Number(liveSignalStats.step_signal_submitted || 0),
      tone: 'emerald',
    },
    {
      key: 'pending',
      label: 'Pending',
      count: Number(liveSignalStats.step_signal_pending || 0),
      tone: Number(liveSignalStats.step_signal_pending || 0) > 0 ? 'amber' : 'emerald',
    },
    {
      key: 'missing_reasoning',
      label: 'Missing reasoning',
      count: Number(liveSignalStats.missing_reasoning || 0),
      tone: Number(liveSignalStats.missing_reasoning || 0) > 0 ? 'amber' : 'slate',
    },
    {
      key: 'missing_confidence',
      label: 'Missing confidence',
      count: Number(liveSignalStats.missing_confidence || 0),
      tone: Number(liveSignalStats.missing_confidence || 0) > 0 ? 'amber' : 'slate',
    },
  ];

  const answerCheckBuckets: BucketChipDefinition[] = hasAnswerKey ? [
    {
      key: 'correct',
      label: 'Correct',
      count: Number(liveSignalStats.correct_count || 0),
      tone: 'emerald',
    },
    {
      key: 'incorrect',
      label: 'Incorrect',
      count: Number(liveSignalStats.incorrect_count || 0),
      tone: Number(liveSignalStats.incorrect_count || 0) > 0 ? 'rose' : 'slate',
    },
    {
      key: 'unscored',
      label: 'Unscored',
      count: Number(liveSignalStats.unscored_count || 0),
      tone: 'slate',
    },
    {
      key: 'high_confidence_wrong',
      label: 'High-confidence wrong',
      count: Number(liveSignalStats.high_confidence_wrong || 0),
      tone: Number(liveSignalStats.high_confidence_wrong || 0) > 0 ? 'rose' : 'slate',
    },
    {
      key: 'low_confidence_wrong',
      label: 'Low-confidence wrong',
      count: Number(liveSignalStats.low_confidence_wrong || 0),
      tone: Number(liveSignalStats.low_confidence_wrong || 0) > 0 ? 'amber' : 'slate',
    },
    {
      key: 'low_confidence_right',
      label: 'Low-confidence right',
      count: Number(liveSignalStats.low_confidence_right || 0),
      tone: Number(liveSignalStats.low_confidence_right || 0) > 0 ? 'sky' : 'slate',
    },
  ] : [];

  return (
    <div data-testid="teacher-signals-panel" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Signals</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900">{currentStepTitle || 'Current step'}</h2>
          <p className="mt-1 text-sm text-slate-600">
            Submitted {Number(liveSignalStats.step_signal_submitted || 0)} of {Number(liveSignalStats.total_students || 0)} students.
          </p>
        </div>
        {currentStepHasSignal && currentStepRevealMode === 'teacher' ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onRevealAnswers}
              disabled={revealSubmitting || revealSuccess}
            >
              {revealSubmitting ? 'Revealing...' : revealSuccess ? 'Revealed' : 'Reveal answers'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="mt-4">
        <p className="text-xs uppercase tracking-wide text-slate-500">Signal status</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {primaryBuckets.map((definition) => renderBucketChip(definition, activeBucket, onSelectBucket))}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs uppercase tracking-wide text-slate-500">Answer check</p>
        {hasAnswerKey ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {answerCheckBuckets.map((definition) => renderBucketChip(definition, activeBucket, onSelectBucket))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">
            Correctness clusters unavailable ({blanksTotal > 0
              ? `Answer key incomplete (${blanksKeyed}/${blanksTotal} keyed).`
              : 'No answer key yet.'})
          </p>
        )}
      </div>

      {activeBucket ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Filtered roster</p>
              <p className="mt-1 text-sm font-medium text-slate-900">
                {activeBucket.split('_').join(' ')} ({bucketNames.length})
              </p>
            </div>
            <button
              type="button"
              className="text-xs text-slate-600 hover:text-slate-900"
              onClick={() => onSelectBucket(null)}
            >
              Clear
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {bucketNames.length > 0 ? bucketNames.map((name, index) => (
              <span
                key={`signal-bucket-name-${name}-${index}`}
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700"
              >
                {name}
              </span>
            )) : (
              <span className="text-sm text-slate-500">No students in this bucket.</span>
            )}
          </div>
        </div>
      ) : null}

      {revealError ? (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {revealError}
        </div>
      ) : null}
    </div>
  );
};
